import { format } from 'date-fns'
import { DownloadIcon, FileTextIcon, RefreshCw } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import { toast } from 'sonner'
import { useMediaQuery } from 'usehooks-ts'

import { Button } from '@/components/ui/button'
import { DataTable, DataTableColumnHeader } from '@/components/ui/data-table'
import { PaginationComponent } from '@/components/ui/pagination'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

import logService from '@/services/log-service'

export const LogsTable = () => {
	const isSmallScreen = useMediaQuery('(max-width: 64rem)')
	const [page, setPage] = useState(1)
	const [rowsPerPage, setRowsPerPage] = useState(50)

	const { data, refetch, isLoading } = useQuery(['fetch-logs'], () => logService.listLogs(), {
		refetchOnWindowFocus: false,
		onError: () => toast.error('Error fetching logs'),
	})

	const allLogs = data?.files || []
	const paginatedLogs = useMemo(() => {
		const start = (page - 1) * rowsPerPage
		return allLogs.slice(start, start + rowsPerPage)
	}, [allLogs, page, rowsPerPage])

	const totalPages = Math.ceil(allLogs.length / rowsPerPage)

	const handleDownload = async (fileName: string) => {
		try {
			const fileContent = await logService.getLogFile(fileName)
			const blob = new Blob([fileContent], { type: 'text/plain' })
			const url = URL.createObjectURL(blob)
			const a = document.createElement('a')
			a.href = url
			a.download = fileName
			a.click()
			URL.revokeObjectURL(url)
			toast.success('Download started!')
		} catch (err: any) {
			toast.error('Failed to download file')
		}
	}

	const MobileLogsTable = () => (
		<Table>
			<TableHeader>
				<TableRow className="bg-[#F7F8F880] border-b-0! py-[13px] px-4 rounded-lg">
					<TableHead className="text-center text-muted-foreground text-[10px] font-medium">
						Datei
					</TableHead>
					<TableHead className="text-center text-muted-foreground text-[10px] font-medium">
						Größe
					</TableHead>
					<TableHead className="text-center text-muted-foreground text-[10px] font-medium">
						Datum
					</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{paginatedLogs.map((log, index) => (
					<TableRow key={index}>
						<TableCell align="center" className="text-xs">
							{log.name}
						</TableCell>
						<TableCell align="center" className="text-xs">
							{log.sizeKB}
						</TableCell>
						<TableCell align="center" className="text-xs">
							{format(new Date(log.modifiedAt), 'dd-MM-yyyy HH:mm')}
						</TableCell>
						<TableCell align="center">
							<Button
								onClick={() => handleDownload(log.name)}
								variant="ghost"
								size="icon"
								className="h-8 w-8">
								<DownloadIcon className="size-4 text-secondary" />
							</Button>
						</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
	)

	return (
		<div className="rounded-[20px] bg-white max-lg:p-4 max-lg:pr-3 lg:px-4 lg:py-6 flex flex-col">
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-2">
					<div className="lg:hidden rounded-xl border border-[#EEEFF0] size-10 flex items-center justify-center">
						<FileTextIcon className="text-secondary" />
					</div>
					<h1 className="text-muted-foreground lg:text-secondary text-xs lg:text-2xl font-medium lg:font-extrabold">
						Booking Logs
					</h1>
				</div>
				<Button variant="outline" size="sm" onClick={() => refetch()}>
					<RefreshCw className="mr-1 size-4" /> Aktualisieren
				</Button>
			</div>

			<div className="flex max-lg:mt-5 mt-6 flex-col gap-3 lg:gap-4">
				<div className="grid grid-cols-2 items-center gap-[9px] lg:hidden">
					<Select
						value={rowsPerPage.toString()}
						onValueChange={value => setRowsPerPage(Number(value))}>
						<SelectTrigger className="w-full h-10 text-xs">
							<SelectValue placeholder="zeigen 50" />
						</SelectTrigger>
						<SelectContent>
							{[50, 80, 100, 200, 500].map(pageSize => (
								<SelectItem key={pageSize} value={pageSize.toString()}>
									zeigen {pageSize}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			</div>

			<div className="mt-4">
				{isSmallScreen ? (
					<MobileLogsTable />
				) : (
					<DataTable
						isLoading={isLoading}
						columns={[
							{
								accessorKey: 'name',
								header: ({ column }) => <DataTableColumnHeader column={column} title="Dateiname" />,
							},
							{
								accessorKey: 'sizeKB',
								header: ({ column }) => (
									<DataTableColumnHeader column={column} title="Größe (KB)" />
								),
							},
							{
								accessorKey: 'modifiedAt',
								header: ({ column }) => (
									<DataTableColumnHeader column={column} title="Geändert am" />
								),
								cell: ({ row }) => (
									<div>{format(new Date(row.getValue('modifiedAt')), 'dd-MM-yyyy HH:mm')}</div>
								),
							},
							{
								id: 'actions',
								enableSorting: false,
								header: 'Aktionen',
								cell: ({ row }) => (
									<div className="flex justify-center">
										<Tooltip>
											<TooltipTrigger
												onClick={() => handleDownload(row.original.name)}
												className="border cursor-pointer border-[#0B224D33] hover:border-secondary size-[33px] flex items-center justify-center rounded-full">
												<DownloadIcon className="size-4 text-secondary" />
											</TooltipTrigger>
											<TooltipContent side="bottom">
												<p className="text-xs font-medium">Herunterladen</p>
											</TooltipContent>
										</Tooltip>
									</div>
								),
							},
						]}
						data={paginatedLogs ?? []}
					/>
				)}
			</div>

			<div className="mt-6 lg:mt-10 flex justify-center">
				<PaginationComponent currentPage={page} totalPages={totalPages} onPageChange={setPage} />
			</div>
		</div>
	)
}

export default LogsTable
